import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";

function parseTags(raw) {
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { return []; } }
  if (!Array.isArray(parsed) || parsed.length === 0) return [];
  if (Array.isArray(parsed[0])) return parsed;
  return [parsed];
}

function buildTrie(questions) {
  const root = { children: new Map(), kpiIds: new Set() };
  for (const q of questions) {
    const combos = parseTags(q.tags);
    for (const combo of combos) {
      if (combo && combo.length) insertCombo(root, combo, q.questionId);
    }
  }
  return root;
}

function insertCombo(node, combo, qId) {
  node.kpiIds.add(qId);
  if (!combo.length) return;
  const tag = combo[0];
  if (!tag || !tag.key || !tag.value) return;
  if (!node.children.has(tag.key)) node.children.set(tag.key, new Map());
  const km = node.children.get(tag.key);
  if (!km.has(tag.value)) km.set(tag.value, { children: new Map(), kpiIds: new Set() });
  insertCombo(km.get(tag.value), combo.slice(1), qId);
}

function traverseTrieMulti(trie, steps) {
  let nodes = [trie];
  for (const step of steps) {
    if (!step?.key || !step?.value) continue;
    const next = [];
    for (const node of nodes) {
      const km = node.children?.get(step.key);
      if (!km) continue;
      const c = km.get(step.value);
      if (c) next.push(c);
    }
    if (!next.length) return [];
    nodes = next;
  }
  return nodes;
}

function getAvailableKeysMulti(nodes, usedKeys) {
  const kvm = new Map();
  for (const node of nodes) {
    if (!node?.children) continue;
    for (const [key, vm] of node.children.entries()) {
      if (usedKeys.has(key)) continue;
      if (!kvm.has(key)) kvm.set(key, new Set());
      for (const v of vm.keys()) kvm.get(key).add(v);
    }
  }
  return Array.from(kvm.entries()).map(([key, values]) => ({
    key,
    values: Array.from(values).sort()
  }));
}

function questionMatchesSteps(q, steps) {
  const active = steps.filter(s => s.values && s.values.size > 0);
  if (!active.length) return true;
  const combos = parseTags(q.tags);
  return combos.some(c => {
    let pos = 0;
    for (const step of active) {
      let found = false;
      for (let i = pos; i < c.length; i++) {
        if (c[i]?.key === step.key && step.values.has(c[i]?.value)) {
          pos = i + 1;
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
    return true;
  });
}

// ─── FIXED: Path-aware cascade filter ─────────────────────────────────────────
// Uses strict prefix matching through combo chains so that:
//   • selecting module=X never surfaces scope/category as next options
//     (module is typically alone in its own combo chain)
//   • selecting scope=scope1 only offers keys that literally follow scope
//     in matching combos (e.g. "category"), not unrelated keys
function getNextEligibleFilters(allQuestions, filterSteps) {
  const active = filterSteps.filter(s => s.values?.size);
  const kvm = new Map();

  for (const q of allQuestions) {
    for (const combo of parseTags(q.tags)) {
      if (!combo?.length) continue;

      if (!active.length) {
        // Root level — expose first key of every combo
        const t = combo[0];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
        continue;
      }

      // Strict prefix: active steps must match combo[0..n-1] in order
      let pos = 0;
      let matched = true;
      for (const step of active) {
        if (
          pos >= combo.length ||
          combo[pos].key !== step.key ||
          !step.values.has(combo[pos].value)
        ) {
          matched = false;
          break;
        }
        pos++;
      }

      // Next key is the one immediately after the matched prefix
      if (matched && pos < combo.length) {
        const t = combo[pos];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
      }
    }
  }

  return Array.from(kvm.entries()).map(([key, values]) => ({
    key,
    values: Array.from(values).sort()
  }));
}

function applyTextSearch(questions, search) {
  if (!search.trim()) return questions;
  const s = search.toLowerCase();
  return questions.filter(q =>
    (q.title || "").toLowerCase().includes(s) ||
    (q.moduleName || "").toLowerCase().includes(s)
  );
}

function buildTagsFromSelected(questions, selectedIds) {
  const seen = new Set(), result = [];
  for (const q of questions) {
    if (!selectedIds.has(q.questionId)) continue;
    const combos = parseTags(q.tags);
    for (const combo of combos) {
      for (const t of combo) {
        if (!t?.key || !t?.value) continue;
        const k = `${t.key}:::${t.value}`;
        if (!seen.has(k)) { seen.add(k); result.push({ key: t.key, value: t.value }); }
      }
    }
  }
  return result;
}

// ─── UI Helpers ───────────────────────────────────────────────────────────────
function Spinner({ size = 14, color = "#fff" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%",
      border: `2px solid ${color}33`, borderTopColor: color,
      animation: "ekpi-spin 0.75s linear infinite", flexShrink: 0
    }} />
  );
}

function Checkbox({ checked, indeterminate, onChange, color = "#3f88a5" }) {
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); onChange(!checked); }}
      style={{
        width: 17, height: 17, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${checked || indeterminate ? color : "#cbd5e1"}`,
        background: checked ? color : indeterminate ? `${color}15` : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", padding: 0, outline: "none",
        fontSize: 10, fontWeight: 900, color: checked ? "#fff" : color,
        transition: "all 0.15s ease", boxShadow: checked ? `0 1px 3px ${color}44` : "none"
      }}
    >
      {checked ? "✓" : indeterminate ? "−" : null}
    </button>
  );
}

// ─── REWRITTEN FilterBar ───────────────────────────────────────────────────────
// Changes vs old version:
//   1. No "Active:" pills row — selected values shown inline on the key button
//   2. Strict cascade: after each selection only the next-in-chain key appears
//   3. Dropdown shows selected/total counts per value (requires pendingIds prop)
//   4. Removing a step also removes all downstream steps (cascade reset)
//   5. Module-group bug fixed via getNextEligibleFilters prefix logic above
function FilterBar({ allQuestions, filterSteps, onFilterStepsChange, pendingIds }) {
  const [openKey, setOpenKey] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const activeSteps = filterSteps.filter(s => s.values?.size);

  // Path-aware: root keys when nothing selected, next-chain key when active
  const eligibleNextFilters = useMemo(
    () => getNextEligibleFilters(allQuestions, filterSteps),
    [allQuestions, filterSteps]
  );

  // Keys that are NOT yet active (to show as "add filter" buttons)
  const nextKeys = useMemo(() => {
    const activeKeys = new Set(activeSteps.map(s => s.key));
    return eligibleNextFilters.filter(k => !activeKeys.has(k.key)).map(k => k.key);
  }, [eligibleNextFilters, activeSteps]);

  // Values for the currently open dropdown
  const openValues = useMemo(() => {
    if (!openKey) return [];
    // For an already-active key: derive values from the combo prefix up to (not including) this step
    const stepIdx = activeSteps.findIndex(s => s.key === openKey);
    const stepsPrefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;

    const vals = new Set();
    for (const q of allQuestions) {
      for (const combo of parseTags(q.tags)) {
        let pos = 0, matched = true;
        for (const step of stepsPrefix) {
          if (pos >= combo.length || combo[pos].key !== step.key || !step.values.has(combo[pos].value)) {
            matched = false; break;
          }
          pos++;
        }
        if (matched && combo[pos]?.key === openKey && combo[pos]?.value) {
          vals.add(combo[pos].value);
        }
      }
    }
    return Array.from(vals).sort();
  }, [openKey, filterSteps, activeSteps, allQuestions]);

  const openSelectedVals = useMemo(() => {
    const s = filterSteps.find(s => s.key === openKey);
    return s?.values ?? new Set();
  }, [filterSteps, openKey]);

  // selected = how many pendingIds questions have key=value in the cascade path
  // total   = how many questions total have key=value in the cascade path
  const getValueCounts = useCallback((key, value) => {
    const stepIdx = activeSteps.findIndex(s => s.key === key);
    const stepsPrefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;

    const matching = allQuestions.filter(q => {
      for (const combo of parseTags(q.tags)) {
        let pos = 0, matched = true;
        for (const step of stepsPrefix) {
          if (pos >= combo.length || combo[pos].key !== step.key || !step.values.has(combo[pos].value)) {
            matched = false; break;
          }
          pos++;
        }
        if (matched && combo[pos]?.key === key && combo[pos]?.value === value) return true;
      }
      return false;
    });

    return {
      total: matching.length,
      selected: matching.filter(q => pendingIds?.has(q.questionId)).length
    };
  }, [allQuestions, activeSteps, pendingIds]);

  const toggleValue = (key, value) => {
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === key);
    if (idx === -1) {
      next.push({ key, values: new Set([value]) });
    } else {
      const vals = next[idx].values;
      if (vals.has(value)) vals.delete(value); else vals.add(value);
      if (!vals.size) {
        // Cascade reset: remove this step and everything after it
        next.splice(idx, next.length - idx);
        setOpenKey(null);
      }
    }
    onFilterStepsChange(next);
  };

  const removeStep = (key) => {
    const idx = filterSteps.findIndex(s => s.key === key);
    if (idx >= 0) {
      onFilterStepsChange(filterSteps.slice(0, idx));
      setOpenKey(null);
    }
  };

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* ── Cascade row ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: "#94a3b8",
          textTransform: "uppercase", letterSpacing: "0.5px"
        }}>
          Filter:
        </span>

        {/* Active steps — show selected values inline on the button, no separate pills */}
        {activeSteps.map((step, i) => {
          const isOpen = openKey === step.key;
          const matchCount = allQuestions.filter(q => questionMatchesSteps(q, filterSteps)).length;
          return (
            <>
              {i > 0 && (
                <span key={`arrow-${step.key}`} style={{ color: "#bae6fd", fontSize: 15, lineHeight: 1 }}>›</span>
              )}
              <button
                key={step.key}
                onClick={() => setOpenKey(isOpen ? null : step.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "5px 8px 5px 10px", borderRadius: 7,
                  border: `1.5px solid ${isOpen ? "#3b82f6" : "#93c5fd"}`,
                  background: isOpen ? "#eff6ff" : "#dbeafe",
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                  color: "#1d4ed8", transition: "all 0.15s"
                }}
              >
                <span style={{ color: "#3b82f6", fontWeight: 700 }}>{step.key}:</span>
                <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {Array.from(step.values).join(", ")}
                </span>
                {/* Match count badge */}
                <span style={{
                  fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 8,
                  background: "#bfdbfe", color: "#1e40af", marginLeft: 2
                }}>
                  {matchCount}
                </span>
                <span style={{ fontSize: 9, color: "#93c5fd" }}>▾</span>
                {/* Remove this step (and cascade) */}
                <span
                  onClick={e => { e.stopPropagation(); removeStep(step.key); }}
                  style={{
                    fontSize: 13, lineHeight: 1, color: "#93c5fd",
                    fontWeight: 900, marginLeft: 1, padding: "0 1px",
                    transition: "color 0.15s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                  onMouseLeave={e => e.currentTarget.style.color = "#93c5fd"}
                >
                  ×
                </span>
              </button>
            </>
          );
        })}

        {/* Arrow from active chain to next available step */}
        {activeSteps.length > 0 && nextKeys.length > 0 && (
          <span style={{ color: "#e2e8f0", fontSize: 15, lineHeight: 1 }}>›</span>
        )}

        {/* Next eligible keys (cascade, not all keys) */}
        {nextKeys.map(key => {
          const isOpen = openKey === key;
          return (
            <button
              key={key}
              onClick={() => setOpenKey(isOpen ? null : key)}
              style={{
                padding: "5px 12px", borderRadius: 7,
                border: `1.5px solid ${isOpen ? "#93c5fd" : "#e2e8f0"}`,
                background: isOpen ? "#eff6ff" : "#fff",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
                color: isOpen ? "#2563eb" : "#64748b",
                transition: "all 0.15s"
              }}
            >
              {key} ▾
            </button>
          );
        })}

        {activeSteps.length > 0 && (
          <button
            onClick={() => { onFilterStepsChange([]); setOpenKey(null); }}
            style={{
              fontSize: 10, color: "#ef4444", border: "none",
              background: "none", cursor: "pointer", fontWeight: 700,
              marginLeft: 4
            }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* ── Value picker with selected/total counts ──────────────────────────── */}
      {openKey && openValues.length > 0 && (
        <div style={{
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
          boxShadow: "0 6px 20px rgba(0,0,0,0.09)", overflow: "hidden",
          maxWidth: 300, maxHeight: 260, display: "flex", flexDirection: "column"
        }}>
          {/* Header */}
          <div style={{
            padding: "7px 12px", background: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexShrink: 0
          }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>
              {openKey}
            </span>
            <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>
              selected / total
            </span>
          </div>

          {/* Values */}
          <div style={{ overflowY: "auto" }}>
            {openValues.map(val => {
              const { total, selected } = getValueCounts(openKey, val);
              const isChecked = openSelectedVals.has(val);
              return (
                <div
                  key={val}
                  onClick={() => toggleValue(openKey, val)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 12px", cursor: "pointer",
                    background: isChecked ? "#f0f9ff" : "transparent",
                    borderBottom: "1px solid #f8fafc",
                    transition: "background 0.1s"
                  }}
                >
                  <Checkbox checked={isChecked} onChange={() => toggleValue(openKey, val)} />
                  <span style={{ flex: 1, fontSize: 12, color: "#374151", fontWeight: isChecked ? 600 : 400 }}>
                    {val}
                  </span>
                  {/* selected / total count badge */}
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: isChecked ? "#dbeafe" : "#f1f5f9",
                    color: isChecked ? "#1d4ed8" : "#64748b",
                    borderRadius: 10, padding: "2px 8px", whiteSpace: "nowrap",
                    border: `1px solid ${isChecked ? "#bfdbfe" : "#e2e8f0"}`
                  }}>
                    {selected}/{total}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KPI List Row — checkbox only, no toggle switch ──────────────────────────
function KpiListRow({ kpi, checked, onChange, isLast }) {
  const combos = parseTags(kpi.tags);
  const allTags = []; const seen = new Set();
  for (const combo of combos) {
    for (const t of combo) {
      if (!t?.key || !t?.value) continue;
      const k = `${t.key}:::${t.value}`;
      if (!seen.has(k)) { seen.add(k); allTags.push(t); }
    }
  }

  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
        borderBottom: isLast ? "none" : "1px solid #f1f5f9",
        cursor: "pointer",
        background: checked ? "#f0f9ff" : "transparent",
        transition: "background 0.1s"
      }}
    >
      <Checkbox checked={checked} onChange={onChange} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 500, color: checked ? "#0c4a6e" : "#1e293b",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
        }} title={kpi.title}>
          {kpi.title || "—"}
        </div>
        {allTags.length > 0 && (
          <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
            {allTags.slice(0, 4).map((t, i) => (
              <span key={i} style={{
                fontSize: 10, color: "#475569",
                background: "#f1f5f9", border: "1px solid #e2e8f0",
                borderRadius: 4, padding: "1px 6px"
              }}>
                {t.value}
              </span>
            ))}
            {allTags.length > 4 && <span style={{ fontSize: 10, color: "#94a3b8" }}>+{allTags.length - 4}</span>}
          </div>
        )}
      </div>
      {checked && (
        <span style={{ fontSize: 9, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 4, padding: "2px 6px" }}>
          SELECTED
        </span>
      )}
    </div>
  );
}

// ─── Selected Exploration Node ──────────────────────────────────────────────
function SelectedDrillNode({ keyName, questions, pendingIds, onToggle, savedIds }) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalPath, setInternalPath] = useState([]);

  const relevantQuestions = useMemo(() => {
    return questions.filter(q => {
      if (!pendingIds.has(q.questionId)) return false;
      const combos = parseTags(q.tags);
      return combos.some(c => c.some(t => t?.key === keyName));
    });
  }, [questions, pendingIds, keyName]);

  const allQuestionsForKey = useMemo(() => {
    return questions.filter(q => {
      const combos = parseTags(q.tags);
      return combos.some(c => c.some(t => t?.key === keyName));
    });
  }, [questions, keyName]);

  const trie = useMemo(() => buildTrie(relevantQuestions), [relevantQuestions]);

  const currentKpis = useMemo(() => {
    if (internalPath.length === 0) return relevantQuestions;
    const nodes = traverseTrieMulti(trie, internalPath);
    const validIds = new Set();
    nodes.forEach(n => n?.kpiIds?.forEach(id => validIds.add(id)));
    return relevantQuestions.filter(q => validIds.has(q.questionId));
  }, [relevantQuestions, internalPath, trie]);

  const topValues = useMemo(() => {
    const vals = new Set();
    const km = trie.children?.get(keyName);
    if (km) for (const v of km.keys()) vals.add(v);
    return Array.from(vals).sort();
  }, [trie, keyName]);

  // Per-value selected/total counts
  const valueStats = useMemo(() => {
    const stats = new Map();
    for (const val of topValues) {
      const total = allQuestionsForKey.filter(q => {
        const combos = parseTags(q.tags);
        return combos.some(c => c.some(t => t?.key === keyName && t?.value === val));
      }).length;
      const selected = allQuestionsForKey.filter(q => {
        if (!pendingIds.has(q.questionId)) return false;
        const combos = parseTags(q.tags);
        return combos.some(c => c.some(t => t?.key === keyName && t?.value === val));
      }).length;
      stats.set(val, { total, selected });
    }
    return stats;
  }, [topValues, allQuestionsForKey, pendingIds, keyName]);

  const nextAvailableKeyData = useMemo(() => {
    if (internalPath.length === 0) return [];
    const nodes = traverseTrieMulti(trie, internalPath);
    const used = new Set([keyName, ...internalPath.map(p => p.key)]);
    return getAvailableKeysMulti(nodes, used);
  }, [trie, internalPath, keyName]);

  const handleDropdownChange = (key, value, index) => {
    const newPath = [...internalPath.slice(0, index)];
    if (value) newPath.push({ key, value });
    setInternalPath(newPath);
  };

  const hasUnsaved = relevantQuestions.some(q => !savedIds.has(q.questionId));
  const selectedCount = relevantQuestions.length;
  const totalForKey = allQuestionsForKey.length;

  return (
    <div style={{ borderBottom: "1px solid #f1f5f9" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "13px 20px",
          cursor: "pointer",
          background: isOpen ? "#f8fafc" : "#fff",
          transition: "background 0.15s",
          userSelect: "none"
        }}
      >
        <div style={{
          width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
          background: isOpen ? "#3f88a5" : "#e2e8f0",
          transition: "all 0.2s", flexShrink: 0
        }}>
          <span style={{ fontSize: 8, color: isOpen ? "#fff" : "#64748b", transform: isOpen ? "rotate(90deg)" : "none", display: "block", transition: "transform 0.2s" }}>▶</span>
        </div>

        <span style={{ fontSize: 11, fontWeight: 700, color: "#3f88a5", textTransform: "uppercase", letterSpacing: "0.8px" }}>
          {keyName}
        </span>

        {hasUnsaved && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#d97706", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 4, padding: "1px 6px" }}>
            UNSAVED
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* ── selected/total count for this group ── */}
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
          background: selectedCount === totalForKey ? "#e0f2fe" : "#fef3c7",
          color: selectedCount === totalForKey ? "#0369a1" : "#d97706",
          border: `1px solid ${selectedCount === totalForKey ? "#bae6fd" : "#fcd34d"}`,
          whiteSpace: "nowrap"
        }}>
          {selectedCount}/{totalForKey}
        </span>
      </div>

      {isOpen && (
        <div style={{ background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>

          {/* Filter Controls — no pills, no title */}
          {topValues.length > 0 && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 8, minHeight: 24 }}>
                {internalPath.length > 0 && (
                  <button
                    onClick={() => setInternalPath([])}
                    style={{
                      fontSize: 10, color: "#ef4444", border: "1px solid #fecaca",
                      background: "#fff5f5", borderRadius: 5, padding: "2px 8px",
                      cursor: "pointer", fontWeight: 600
                    }}
                  >
                    ✕ Reset
                  </button>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    {keyName}
                  </label>
                  <select
                    style={{
                      width: "100%", padding: "7px 10px", borderRadius: 7,
                      border: `1.5px solid ${internalPath[0]?.key === keyName ? "#93c5fd" : "#e2e8f0"}`,
                      fontSize: 12, outline: "none", background: "#fff", color: "#374151",
                      cursor: "pointer"
                    }}
                    value={internalPath[0]?.key === keyName ? internalPath[0].value : ""}
                    onChange={e => handleDropdownChange(keyName, e.target.value, 0)}
                  >
                    <option value="">All {keyName}s</option>
                    {topValues.map(v => {
                      const stat = valueStats.get(v) || { selected: 0, total: 0 };
                      return <option key={v} value={v}>{v}  ({stat.selected}/{stat.total})</option>;
                    })}
                  </select>
                </div>

                {internalPath.length > 1 && internalPath.slice(1).map((step, idx) => (
                  <div key={step.key} style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      {step.key}
                    </label>
                    <select
                      style={{
                        width: "100%", padding: "7px 10px", borderRadius: 7,
                        border: "1.5px solid #93c5fd", fontSize: 12,
                        outline: "none", background: "#fff", color: "#374151", cursor: "pointer"
                      }}
                      value={step.value}
                      onChange={e => handleDropdownChange(step.key, e.target.value, idx + 1)}
                    >
                      <option value="">All {step.key}s</option>
                      <option value={step.value}>{step.value}</option>
                    </select>
                  </div>
                ))}

                {internalPath.length > 0 && nextAvailableKeyData.length > 0 && (
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "#3f88a5", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      {nextAvailableKeyData[0].key}
                    </label>
                    <select
                      style={{
                        width: "100%", padding: "7px 10px", borderRadius: 7,
                        border: "1.5px solid #bae6fd", fontSize: 12, outline: "none",
                        background: "#f0f9ff", color: "#0369a1", cursor: "pointer"
                      }}
                      value=""
                      onChange={e => {
                        const [k, v] = e.target.value.split(":::");
                        if (k && v) handleDropdownChange(k, v, internalPath.length);
                      }}
                    >
                      <option value="">Select {nextAvailableKeyData[0].key}...</option>
                      {nextAvailableKeyData.map(group => (
                        <optgroup key={group.key} label={group.key.toUpperCase()}>
                          {group.values.map(val => (
                            <option key={`${group.key}-${val}`} value={`${group.key}:::${val}`}>{val}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {internalPath.length > 0 && (
                <div style={{
                  marginTop: 10, display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 12px", background: "#f0f9ff",
                  borderRadius: 8, border: "1px solid #bae6fd"
                }}>
                  {/* Path breadcrumb */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, flexWrap: "wrap" }}>
                    {internalPath.map((step, i) => (
                      <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {i > 0 && <span style={{ color: "#bae6fd", fontSize: 13 }}>›</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8" }}>
                          {step.value}
                        </span>
                      </span>
                    ))}
                  </div>
                  {/* KPI availability summary */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: "#64748b" }}>selected</span>
                    <span style={{
                      fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 10,
                      background: "#0369a1", color: "#fff"
                    }}>
                      {currentKpis.filter(q => pendingIds.has(q.questionId)).length}
                    </span>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>/</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
                      background: "#e0f2fe", color: "#0369a1",
                      border: "1px solid #bae6fd"
                    }}>
                      {currentKpis.length}
                    </span>
                    <span style={{ fontSize: 10, color: "#64748b" }}>available</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* KPI List */}
          <div style={{ margin: "0 20px 16px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            {currentKpis.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                No KPIs match this filter.
              </div>
            ) : (
              <>
                <div style={{
                  padding: "8px 14px", background: "#f8fafc",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex", alignItems: "center", justifyContent: "space-between"
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    KPI Title
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    {currentKpis.length} item{currentKpis.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {currentKpis.map((q, idx) => {
                  const isUnsaved = !savedIds.has(q.questionId);
                  return (
                    <div
                      key={q.questionId}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "10px 14px",
                        borderBottom: idx === currentKpis.length - 1 ? "none" : "1px solid #f8fafc",
                        transition: "background 0.1s",
                        background: "#fff"
                      }}
                    >
                      <Checkbox
                        checked={pendingIds.has(q.questionId)}
                        onChange={c => onToggle(q.questionId, c)}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                          fontSize: 12.5, color: "#1e293b", fontWeight: 450,
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap", display: "block"
                        }} title={q.title}>
                          {q.title}
                        </span>
                      </div>
                      {isUnsaved && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: "#d97706",
                          background: "#fef3c7", border: "1px solid #fcd34d",
                          borderRadius: 4, padding: "1px 6px", flexShrink: 0
                        }}>
                          UNSAVED
                        </span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Selected View ────────────────────────────────────────────────────────────
function SelectedKpisAccordion({ questions, savedIds, pendingIds, onToggle }) {
  const topLevelKeys = useMemo(() => {
    const keys = new Set();
    questions.filter(q => pendingIds.has(q.questionId)).forEach(q => {
      const combos = parseTags(q.tags);
      combos.forEach(c => { if (c[0]?.key) keys.add(c[0].key); });
    });
    return Array.from(keys).sort();
  }, [questions, pendingIds]);

  if (pendingIds.size === 0) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>○</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>No KPIs selected</div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Switch to the All KPIs tab to add KPIs.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        padding: "10px 20px", background: "#f0f9ff",
        borderBottom: "1px solid #bae6fd",
        display: "flex", alignItems: "center", gap: 5
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>{pendingIds.size}</span>
        <span style={{ fontSize: 13, color: "#64748b" }}>selected out of</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>{questions.length}</span>
      </div>

      {topLevelKeys.map(key => (
        <SelectedDrillNode
          key={key}
          keyName={key}
          questions={questions}
          pendingIds={pendingIds}
          onToggle={onToggle}
          savedIds={savedIds}
        />
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function EmissionKPISettings() {
  const [allQuestions, setAllQuestions] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [isEnabled, setIsEnabled] = useState(true);
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [kpiView, setKpiView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState(null);

  const fyData = JSON.parse(localStorage.getItem("financialYears"));
  const latestFY = getLatestFY(fyData);
  const fyId = latestFY?.id;

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const trie = useMemo(() => buildTrie(allQuestions), [allQuestions]);

  const filtered = useMemo(() => allQuestions.filter(q => questionMatchesSteps(q, filterSteps)), [allQuestions, filterSteps]);
  const displayed = useMemo(() => applyTextSearch(filtered, search), [filtered, search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, {}, "GET"),
      ]);
      const userId = JSON.parse(localStorage.getItem("user_temp_id"));

      const fwRes = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {}, { type: "ALL", userId }, "GET");
      const frameworks = fwRes.data?.data || [];
      const qRes = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestion`, {}, { financialYearId: fyId, frameworkIds: frameworks.map(f => f.id), fromSettings: true }, "GET");
      const questions = qRes.data?.data || [];
      setAllQuestions(questions);

      const raw = settingsRes.data;
      setIsEnabled(raw?.is_enabled ?? true);
      const savedK = Array.isArray(raw?.kpiIds) ? raw.kpiIds.map(Number) : [];
      const ids = raw?.id ? new Set(savedK) : new Set(questions.filter(q => q.kpi_type?.includes("EMISSION")).map(q => q.questionId));
      setSavedIds(ids); setPendingIds(new Set(ids));
    } catch { showToast("Error loading data", "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleKpi = (id, checked) => {
    setPendingIds(p => { const n = new Set(p); checked ? n.add(id) : n.delete(id); return n; });
  };

  const handleSave = async () => {
    setSaving(true);
    const kpiIds = Array.from(pendingIds);
    const tags = buildTagsFromSelected(allQuestions, pendingIds);
    const { isSuccess } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { kpiIds, tags, is_enabled: isEnabled, fyid: fyId }, "POST");
    if (isSuccess) {
      setSavedIds(new Set(pendingIds)); setSaved(true);
      showToast("Preferences saved!");
      setTimeout(() => setSaved(false), 2000);
    }
    setSaving(false);
  };

  const hasPendingChanges = useMemo(() => {
    if (pendingIds.size !== savedIds.size) return true;
    for (const id of pendingIds) if (!savedIds.has(id)) return true;
    return false;
  }, [pendingIds, savedIds]);

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI preferences…</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        @keyframes ekpi-spin { to { transform: rotate(360deg) } }
        @keyframes ekpi-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        select:focus { outline: none; }
        button:focus-visible { outline: 2px solid #3f88a5; outline-offset: 2px; }
      `}</style>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10,
          zIndex: 1000, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          animation: "ekpi-toast-in 0.25s ease",
          display: "flex", alignItems: "center", gap: 8
        }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span>
          {toast.msg}
        </div>
      )}

      {/* Top Bar */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, #3f88a5, #2563eb)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Emission KPI Preferences</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {pendingIds.size} KPI{pendingIds.size !== 1 ? "s" : ""} selected
              {hasPendingChanges && <span style={{ color: "#d97706", fontWeight: 600 }}> · unsaved changes</span>}
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !hasPendingChanges}
          style={{
            background: hasPendingChanges ? "#3f88a5" : "#e2e8f0",
            color: hasPendingChanges ? "#fff" : "#94a3b8",
            border: "none", padding: "9px 22px", borderRadius: 8,
            cursor: hasPendingChanges ? "pointer" : "default",
            fontSize: 13, fontWeight: 600,
            display: "flex", alignItems: "center", gap: 8,
            transition: "all 0.2s",
            boxShadow: hasPendingChanges ? "0 2px 8px rgba(63,136,165,0.35)" : "none"
          }}
        >
          {saving ? <><Spinner size={12} color="#fff" /> Saving…</> : saved ? "✓ Saved!" : "Save Preferences"}
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: "24px", maxWidth: 1040, margin: "0 auto" }}>

        <div style={{
          display: "flex", gap: 0, background: "#e2e8f0", padding: 3,
          borderRadius: 10, width: "fit-content", marginBottom: 20
        }}>
          {[
            { key: "list", label: "All KPIs", count: allQuestions.length },
            { key: "selected", label: "Selected", count: pendingIds.size }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setKpiView(tab.key)}
              style={{
                padding: "7px 20px", border: "none", borderRadius: 8,
                background: kpiView === tab.key ? "#fff" : "transparent",
                cursor: "pointer", fontSize: 12, fontWeight: 600,
                color: kpiView === tab.key ? "#0f172a" : "#64748b",
                display: "flex", alignItems: "center", gap: 7,
                boxShadow: kpiView === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.15s"
              }}
            >
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key
                  ? (tab.key === "selected" ? "#dbeafe" : "#f1f5f9")
                  : "#d1d5db",
                color: kpiView === tab.key
                  ? (tab.key === "selected" ? "#1d4ed8" : "#475569")
                  : "#94a3b8"
              }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* All KPIs Tab Controls */}
        {kpiView === "list" && (
          <div style={{
            background: "#fff", padding: "16px", borderRadius: 12,
            border: "1px solid #e2e8f0", marginBottom: 12,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
          }}>
            <div style={{ position: "relative", marginBottom: 12 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>⌕</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by title or module…"
                style={{
                  width: "100%", padding: "9px 12px 9px 32px",
                  border: "1.5px solid #e2e8f0", borderRadius: 9,
                  fontSize: 13, outline: "none", color: "#1e293b",
                  transition: "border-color 0.15s"
                }}
                onFocus={e => e.target.style.borderColor = "#93c5fd"}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>
            {/* FilterBar: trie removed, pendingIds added */}
            <FilterBar
              allQuestions={allQuestions}
              filterSteps={filterSteps}
              onFilterStepsChange={setFilterSteps}
              pendingIds={pendingIds}
            />
          </div>
        )}

        {/* Results Summary */}
        {kpiView === "list" && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "0 2px" }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              Showing {displayed.length} of {allQuestions.length} KPIs
              {filterSteps.some(s => s.values?.size) || search ? " (filtered)" : ""}
            </span>
            {(filterSteps.some(s => s.values?.size) || search) && (
              <button
                onClick={() => { setFilterSteps([]); setSearch(""); }}
                style={{ fontSize: 11, color: "#ef4444", border: "none", background: "none", cursor: "pointer", fontWeight: 600 }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* KPI List / Selected Panel */}
        <div style={{
          background: "#fff", borderRadius: 12,
          border: "1px solid #e2e8f0", overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
        }}>
          {kpiView === "list" ? (
            displayed.length === 0 ? (
              <div style={{ padding: "60px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>No KPIs match your search or filters.</div>
              </div>
            ) : (
              <div style={{ maxHeight: 580, overflowY: "auto" }}>
                {displayed.map((q, idx) => (
                  <KpiListRow
                    key={q.questionId}
                    kpi={q}
                    checked={pendingIds.has(q.questionId)}
                    onChange={c => toggleKpi(q.questionId, c)}
                    isLast={idx === displayed.length - 1}
                  />
                ))}
              </div>
            )
          ) : (
            <SelectedKpisAccordion
              questions={allQuestions}
              savedIds={savedIds}
              pendingIds={pendingIds}
              onToggle={toggleKpi}
            />
          )}
        </div>
      </div>
    </div>
  );
}